import { selectLicenseProductState } from '../../../reducers/SmartDentReducer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { SmartDentAction } from '../../../actions/SmartDentAction';
import {
    LICENSE_PRODUCT_SMARTDENT,
    LICENSE_PRODUCTS,
} from '../../../actions/constants/LicenseProductConstant';
import { Translate as t } from '../../../config/Translate';
import { Loading } from '../../../components/common/Loading';
import { canViewDealer, canViewIssuance, canViewSubsidiary, getLicenseRole } from '../../../lib/licenseHelper';
import { ROLE_INTERNAL_DEALER, ROLE_EXTERNAL_DEALER } from '../../../config/appConstants';
import LicenseDealerTab from './LicenseDealerTab';
import LicenseIssuanceTab from './LicenseIssuanceTab';
import LicenseOverviewTab from './LicenseOverviewTab';
import LicenseSubsidiaryTab from './LicenseSubsidiaryTab';
import '../../../asset/css/admin.css';

export const TABS = {
    SUBSIDIARY: 'subsidiary',
    ISSUANCE: 'issuance',
    DEALER: 'dealer',
};

// IMP-176: role 기반 default 탭 + URL ?tab=X 값 검증.
//   - tab 값이 invalid(옛 ?tab=overview 포함) 또는 권한 없으면 경고 없이 role default 반환
//   - 새로고침 / 북마크 / 링크 공유 시 동일 탭 노출
export const getDefaultTab = (role) => {
    if (role === ROLE_EXTERNAL_DEALER || role === ROLE_INTERNAL_DEALER) return TABS.DEALER;
    return TABS.SUBSIDIARY;
};

export const resolveTabFromQuery = (search, role) => {
    const fallback = getDefaultTab(role);
    if (typeof search !== 'string') return fallback;
    try {
        const params = new URLSearchParams(search);
        const requested = params.get('tab');
        if (!requested) return fallback;
        // 권한 가드 — invalid/권한없는 tab 은 default fallback.
        if (requested === TABS.SUBSIDIARY && canViewSubsidiary(role)) return TABS.SUBSIDIARY;
        if (requested === TABS.ISSUANCE && canViewIssuance(role)) return TABS.ISSUANCE;
        if (requested === TABS.DEALER && canViewDealer(role)) return TABS.DEALER;
        return fallback;
    } catch {
        return fallback;
    }
};

// URL ?product=<key> 원본 값. 없거나 빈 값이거나 문자열이 아니면 null.
const readProductQuery = (search) => {
    if (typeof search !== 'string') return null;
    try {
        return new URLSearchParams(search).get('product') || null;
    } catch {
        return null;
    }
};

// FEAT-059: ?product= 로 허브의 제품을 정한다. 순수 함수 — 값 없음·목록 밖은 null(미선택, 경고는 컴포넌트가 낸다).
export const resolveProductFromQuery = (search) => {
    const raw = readProductQuery(search);
    return LICENSE_PRODUCTS.find((p) => p.key === raw) ?? null;
};

const LicenseHub = ({ user }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const role = getLicenseRole(user);
    const product = resolveProductFromQuery(location.search);
    const rawProductKey = useMemo(() => readProductQuery(location.search), [location.search]);
    // 미선택(product null)이면 선택자를 부르지 않는다 — 기본 제품 상태로 폴백하지 않게.
    const packages = useSelector((state) => (product ? selectLicenseProductState(state, product.key).packages : undefined));
    const selectedPackage = useSelector((state) => (product ? selectLicenseProductState(state, product.key).selectedPackage : undefined));
    const [activeTab, setActiveTabRaw] = useState(() => resolveTabFromQuery(location.search, role));

    // IMP-176: 탭 변경 시 URL ?tab=X 동기화. history.replace 로 back stack 오염 방지.
    const setActiveTab = useCallback(
        (next) => {
            setActiveTabRaw(next);
            const params = new URLSearchParams(location.search);
            params.set('tab', next);
            history.replace({ pathname: location.pathname, search: `?${params.toString()}` });
        },
        [history, location.pathname, location.search],
    );
    // 왼쪽 메뉴·상단 버튼은 ?tab= 없이 진입하므로 search 변경 시 탭 재계산.
    // setActiveTab 의 history.replace 가 낸 search 는 같은 탭으로 해석되어 되먹임 없음.
    useEffect(() => {
        setActiveTabRaw(resolveTabFromQuery(location.search, role));
    }, [location.search, role]);

    const [hubError, setHubError] = useState(null);
    const [packagesLoaded, setPackagesLoaded] = useState(false);

    // 목록 밖 ?product= 는 미선택으로 둔다 — URL 은 고치지 않으므로 값이 바뀔 때 1회 알린다.
    useEffect(() => {
        if (!rawProductKey || LICENSE_PRODUCTS.some((p) => p.key === rawProductKey)) return;
        console.warn('[guard] LicenseHub: unknown product query', {
            handler: 'LicenseHub.product',
            callerCompanyId: user?.companyId ?? null,
            target: rawProductKey,
            reason: 'not in LICENSE_PRODUCTS — leave product unselected',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawProductKey]);

    // 제품이 바뀌면 로딩 상태를 되돌리고, 이전 제품의 늦은 응답은 무시한다. 미선택이면 조회하지 않는다.
    useEffect(() => {
        setPackagesLoaded(false);
        if (!product) return undefined;
        let alive = true;
        dispatch(SmartDentAction.GetPackages(product)).then(() => {
            if (alive) setPackagesLoaded(true);
        });
        return () => { alive = false; };
    }, [dispatch, product]);

    // auto-select single package
    useEffect(() => {
        if (product && packages && packages.length === 1 && !selectedPackage) {
            dispatch(SmartDentAction.GetPackage(packages[0]._id, product));
        }
    }, [dispatch, packages, selectedPackage, product]);

    const showSubsidiary = canViewSubsidiary(role);
    const showIssuance = canViewIssuance(role);
    const showDealer = canViewDealer(role);

    if (!role) {
        return <div className="alert alert-warning">{t('common.message.noAccess')}</div>;
    }

    // 왼쪽 카드의 제품 선택 — 다른 search params(tab 등)는 그대로 두고 product 만 바꾼다.
    const handleSelectProduct = (productKey) => {
        const params = new URLSearchParams(location.search);
        params.set('product', productKey);
        history.replace({ pathname: location.pathname, search: `?${params.toString()}` });
    };

    const renderTabContent = () => {
        // Subsidiary / Dealer 탭은 selectedPackage 필요
        if (!packagesLoaded) {
            return (
                <div className="d-flex justify-content-center py-5">
                    <Loading />
                </div>
            );
        }
        if (!packages || packages.length === 0) {
            return (
                <div className="alert alert-warning mt-3">
                    {t('license.noPackages')}
                    {product.key === LICENSE_PRODUCT_SMARTDENT.key && (
                        <>
                            {' — '}
                            <small className="text-muted">{t('license.message.contactHqForSmartDentPackage')}</small>
                        </>
                    )}
                </div>
            );
        }
        if (!selectedPackage) {
            return (
                <div className="d-flex justify-content-center py-5">
                    <Loading />
                </div>
            );
        }

        if (activeTab === TABS.SUBSIDIARY && showSubsidiary) {
            return (
                <LicenseSubsidiaryTab
                    key={product.key}
                    product={product}
                    user={user}
                    role={role}
                    selectedPackage={selectedPackage}
                />
            );
        }
        if (activeTab === TABS.ISSUANCE && showIssuance) {
            return (
                <LicenseIssuanceTab
                    key={product.key}
                    product={product}
                    user={user}
                    role={role}
                    selectedPackage={selectedPackage}
                />
            );
        }
        if (activeTab === TABS.DEALER && showDealer) {
            return (
                <LicenseDealerTab
                    key={product.key}
                    product={product}
                    user={user}
                    role={role}
                    selectedPackage={selectedPackage}
                />
            );
        }
        return null;
    };

    return (
        <div id="appCapsule" className="full-height">
        <div className="container-fluid pt-1">
            {hubError && (
                <div className="alert alert-danger alert-dismissible">
                    {hubError}
                    <button type="button" className="btn-close" onClick={() => setHubError(null)} />
                </div>
            )}

            {/* 제목 줄: 라이선스 현황 제목. 왼쪽 카드 열은 제품·패키지 조회와 무관하게 항상 둔다. */}
            <h5 className="mb-2 fw-bold">{t('license.label.overview')}</h5>

            <div className="row g-2">
                <div className="col-12 license-status-col">
                    <LicenseOverviewTab
                        user={user}
                        role={role}
                        selectedProductKey={product?.key ?? null}
                        onSelectProduct={handleSelectProduct}
                    />
                </div>
                <div className="col-12 license-tabs-col">
                    {product && (
                        <>
                            {/* 고른 제품 이름 | 줄 끝 패키지 select(복수일 때만) */}
                            <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                                <h6 className="mb-0 fw-bold">{product.name}</h6>
                                {packages && packages.length > 1 && (
                                    <select
                                        className="form-select form-select-sm w-auto ms-auto"
                                        value={selectedPackage?._id || ''}
                                        onChange={(e) => {
                                            const pkg = packages.find((p) => p._id === e.target.value);
                                            if (pkg) dispatch(SmartDentAction.GetPackage(pkg._id, product));
                                        }}
                                    >
                                        <option value="">{t('license.button.selectPackage')}</option>
                                        {packages.map((p) => (
                                            <option key={p._id} value={p._id}>{p.sk || p._id}</option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            {/* 탭 네비게이션 */}
                            <ul className="nav nav-tabs style2 mt-1 mb-2">
                                {showSubsidiary && (
                                    <li className="nav-item">
                                        <button
                                            className={activeTab === TABS.SUBSIDIARY ? 'nav-link active' : 'nav-link'}
                                            onClick={() => setActiveTab(TABS.SUBSIDIARY)}
                                        >
                                            {t('company.label.subsidiaryManagement')}
                                        </button>
                                    </li>
                                )}
                                {showIssuance && (
                                    <li className="nav-item">
                                        <button
                                            className={activeTab === TABS.ISSUANCE ? 'nav-link active' : 'nav-link'}
                                            onClick={() => setActiveTab(TABS.ISSUANCE)}
                                        >
                                            {t('license.tab.issuanceStatus')}
                                        </button>
                                    </li>
                                )}
                                {showDealer && (
                                    <li className="nav-item">
                                        <button
                                            className={activeTab === TABS.DEALER ? 'nav-link active' : 'nav-link'}
                                            onClick={() => setActiveTab(TABS.DEALER)}
                                        >
                                            {t('license.tab.myIssuance')}
                                        </button>
                                    </li>
                                )}
                            </ul>

                            <div className="tab-content">
                                {renderTabContent()}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
        </div>
    );
};

const mapState = (state) => ({
    user: state.AuthReducer.user,
});

export default connect(mapState)(LicenseHub);
